/**
 * 废钢直通车 - 状态常量定义
 * 统一用户版(Client)和商家版(Workbench)的状态定义
 */

// ============ 订单状态 ============
export const ORDER_STATUS = {
  PENDING_SIGN: 'pending_sign',           // 待签署 - 等待供应商签署
  PENDING_APPROVAL: 'pending_approval',   // 待审批 - 等待商家审批
  REJECTED: 'rejected',                   // 已驳回 - 商家驳回
  IN_PROGRESS: 'in_progress',             // 进行中 - 审批通过，可正常入库
  COMPLETED: 'completed',                 // 已完成 - 全部送货单已付款且达到截止条件
  CANCELLED: 'cancelled',                 // 已取消 - 订单取消
};

// 订单状态显示映射
export const ORDER_STATUS_LABELS = {
  // 用户版（Client）
  client: {
    [ORDER_STATUS.PENDING_SIGN]: '待签署',
    [ORDER_STATUS.PENDING_APPROVAL]: '进行中',   // 用户版显示"进行中"
    [ORDER_STATUS.REJECTED]: '已取消',           // 用户版显示"已取消"
    [ORDER_STATUS.IN_PROGRESS]: '进行中',
    [ORDER_STATUS.COMPLETED]: '已完成',
    [ORDER_STATUS.CANCELLED]: '已取消',
  },
  // 商家版（Workbench）
  workbench: {
    [ORDER_STATUS.PENDING_SIGN]: '待签署',
    [ORDER_STATUS.PENDING_APPROVAL]: '待审批',
    [ORDER_STATUS.REJECTED]: '已驳回',
    [ORDER_STATUS.IN_PROGRESS]: '进行中',
    [ORDER_STATUS.COMPLETED]: '已完成',
    [ORDER_STATUS.CANCELLED]: '已取消',
  },
};

// 订单状态颜色（商家版）
export const ORDER_STATUS_COLORS = {
  [ORDER_STATUS.PENDING_SIGN]: { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-500' },
  [ORDER_STATUS.PENDING_APPROVAL]: { bg: 'bg-orange-100', text: 'text-orange-600', dot: 'bg-orange-500' },
  [ORDER_STATUS.REJECTED]: { bg: 'bg-red-100', text: 'text-red-600', dot: 'bg-red-500' },
  [ORDER_STATUS.IN_PROGRESS]: { bg: 'bg-blue-100', text: 'text-blue-600', dot: 'bg-blue-500' },
  [ORDER_STATUS.COMPLETED]: { bg: 'bg-green-100', text: 'text-green-600', dot: 'bg-green-500' },
  [ORDER_STATUS.CANCELLED]: { bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-500' },
};

// ============ 送货单状态 ============
export const DELIVERY_STATUS = {
  WAITING_DELIVERY: 'waiting_delivery',     // 待送货 - 等待发货
  ARRIVED: 'arrived',                       // 已到场 - 车辆已到达基地
  WAITING_APPROVAL: 'waiting_approval',     // 等待审批 - 车辆等待订单审批通过
  STORED: 'stored',                         // 已入库 - 过磅入库完成
  PENDING_SETTLEMENT: 'pending_settlement', // 待结算 - 等待供应商确认结算金额
  SETTLED: 'settled',                       // 已结算 - 供应商已确认结算
  INVOICED: 'invoiced',                     // 已开票 - 发票已开具
  PAID: 'paid',                             // 已付款 - 款项已支付
};

// 送货单状态显示映射（用户版和商家版一致）
export const DELIVERY_STATUS_LABELS = {
  [DELIVERY_STATUS.WAITING_DELIVERY]: '待送货',
  [DELIVERY_STATUS.ARRIVED]: '已到场',
  [DELIVERY_STATUS.WAITING_APPROVAL]: '等待审批',
  [DELIVERY_STATUS.STORED]: '已入库',
  [DELIVERY_STATUS.PENDING_SETTLEMENT]: '待结算',
  [DELIVERY_STATUS.SETTLED]: '已结算',
  [DELIVERY_STATUS.INVOICED]: '已开票',
  [DELIVERY_STATUS.PAID]: '已付款',
};

// 送货单状态颜色
export const DELIVERY_STATUS_COLORS = {
  [DELIVERY_STATUS.WAITING_DELIVERY]: { bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-500', icon: 'Truck' },
  [DELIVERY_STATUS.ARRIVED]: { bg: 'bg-blue-100', text: 'text-blue-600', dot: 'bg-blue-500', icon: 'MapPin' },
  [DELIVERY_STATUS.WAITING_APPROVAL]: { bg: 'bg-amber-100', text: 'text-amber-600', dot: 'bg-amber-500', icon: 'AlertCircle' },
  [DELIVERY_STATUS.STORED]: { bg: 'bg-purple-100', text: 'text-purple-600', dot: 'bg-purple-500', icon: 'Package' },
  [DELIVERY_STATUS.PENDING_SETTLEMENT]: { bg: 'bg-orange-100', text: 'text-orange-600', dot: 'bg-orange-500', icon: 'DollarSign' },
  [DELIVERY_STATUS.SETTLED]: { bg: 'bg-amber-100', text: 'text-amber-600', dot: 'bg-amber-500', icon: 'CheckCircle' },
  [DELIVERY_STATUS.INVOICED]: { bg: 'bg-teal-100', text: 'text-teal-600', dot: 'bg-teal-500', icon: 'Receipt' },
  [DELIVERY_STATUS.PAID]: { bg: 'bg-green-100', text: 'text-green-600', dot: 'bg-green-500', icon: 'CreditCard' },
};

// 送货单状态顺序（用于进度条）
export const DELIVERY_STATUS_ORDER = [
  DELIVERY_STATUS.WAITING_DELIVERY,
  DELIVERY_STATUS.ARRIVED,
  DELIVERY_STATUS.WAITING_APPROVAL,
  DELIVERY_STATUS.STORED,
  DELIVERY_STATUS.PENDING_SETTLEMENT,
  DELIVERY_STATUS.SETTLED,
  DELIVERY_STATUS.INVOICED,
  DELIVERY_STATUS.PAID,
];

// ============ 业务逻辑函数 ============

/**
 * 检查是否可以入库
 * @param {Object} delivery 送货单
 * @param {Object} order 关联的订单
 * @returns {Object} { canStore: boolean, reason: string }
 */
export const canStoreDelivery = (delivery, order) => {
  // 已入库的不能再入库
  if (delivery.status === DELIVERY_STATUS.STORED) {
    return { canStore: false, reason: '该送货单已入库' };
  }

  // 未到场的不能入库
  if (delivery.status !== DELIVERY_STATUS.ARRIVED &&
      delivery.status !== DELIVERY_STATUS.WAITING_APPROVAL) {
    return { canStore: false, reason: '车辆尚未到场' };
  }

  // 检查订单状态
  const approvedStatuses = [ORDER_STATUS.IN_PROGRESS];
  if (!approvedStatuses.includes(order.status)) {
    return {
      canStore: false,
      reason: '订单尚未审批通过，请等待审批完成后再进行入库',
      orderStatus: order.status,
    };
  }

  return { canStore: true, reason: null };
};

/**
 * 判断订单是否可以标记为已完成
 * @param {Object} order 订单
 * @returns {boolean}
 */
export const checkOrderComplete = (order) => {
  // 1. 检查所有送货单是否已付款
  const { deliveryStats } = order;
  if (!deliveryStats || deliveryStats.total === 0) return false;

  const allPaid = deliveryStats.paid === deliveryStats.total;
  if (!allPaid) return false;

  // 2. 检查截止条件（满足其一即可）
  const now = new Date();
  const deadline = order.deliveryDeadline ? new Date(order.deliveryDeadline) : null;
  const isPastDeadline = deadline && now >= deadline;

  const isWeightReached = order.actualWeight >= order.estimatedWeight;

  return isPastDeadline || isWeightReached;
};

/**
 * 计算订单的送货统计
 * @param {Array} deliveries 送货单列表
 * @returns {Object} 统计数据
 */
export const calculateDeliveryStats = (deliveries) => {
  if (!deliveries || deliveries.length === 0) {
    return {
      total: 0,
      waitingDelivery: 0,
      arrived: 0,
      waitingApproval: 0,
      stored: 0,
      pendingSettlement: 0,
      settled: 0,
      invoiced: 0,
      paid: 0,
    };
  }

  return {
    total: deliveries.length,
    waitingDelivery: deliveries.filter(d => d.status === DELIVERY_STATUS.WAITING_DELIVERY).length,
    arrived: deliveries.filter(d => d.status === DELIVERY_STATUS.ARRIVED).length,
    waitingApproval: deliveries.filter(d => d.status === DELIVERY_STATUS.WAITING_APPROVAL).length,
    stored: deliveries.filter(d => d.status === DELIVERY_STATUS.STORED).length,
    pendingSettlement: deliveries.filter(d => d.status === DELIVERY_STATUS.PENDING_SETTLEMENT).length,
    settled: deliveries.filter(d => d.status === DELIVERY_STATUS.SETTLED).length,
    invoiced: deliveries.filter(d => d.status === DELIVERY_STATUS.INVOICED).length,
    paid: deliveries.filter(d => d.status === DELIVERY_STATUS.PAID).length,
  };
};

/**
 * 获取送货单状态进度百分比
 * @param {String} status 当前状态
 * @returns {Number} 百分比 0-100
 */
export const getDeliveryProgress = (status) => {
  const index = DELIVERY_STATUS_ORDER.indexOf(status);
  if (index === -1) return 0;
  // 跳过 WAITING_APPROVAL（这是异常状态，不算进度）
  const normalOrder = DELIVERY_STATUS_ORDER.filter(s => s !== DELIVERY_STATUS.WAITING_APPROVAL);
  const normalIndex = normalOrder.indexOf(status);
  return Math.round((normalIndex + 1) / normalOrder.length * 100);
};
